import type { FileRejection } from 'react-dropzone';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { fData } from 'src/utils/format-number';

import { uploadClasses } from '../classes';
import { fileData } from '../../file-thumbnail';

// ----------------------------------------------------------------------

type RejectionFilesProps = React.ComponentProps<typeof ListRoot> & {
  files?: readonly FileRejection[];
};

// react-dropzone's default "file-invalid-type" message lists raw MIME types alongside
// extensions (e.g. "application/vnd.openxmlformats-…, .docx"). Show only the extensions users
// recognize; other error codes (size, count, …) pass through unchanged.
function friendlyError(error: { code: string; message: string }) {
  if (error.code === 'file-invalid-type') {
    // The message is a comma-separated list mixing MIME types and extensions. Keep only the
    // tokens that are *entirely* an extension (".pdf"), so dotted MIME segments like
    // ".openxmlformats" / ".wordprocessingml" aren't mistaken for extensions.
    const extensions = Array.from(
      new Set(
        error.message
          .split(',')
          .map((token) => token.trim().toLowerCase())
          .filter((token) => /^\.[a-z0-9]+$/.test(token))
      )
    );
    if (extensions.length) {
      return `File type not supported. Allowed types: ${extensions.join(', ')}.`;
    }
  }
  return error.message;
}

export function RejectionFiles({ files, sx, className, ...other }: RejectionFilesProps) {
  return (
    <ListRoot
      className={mergeClasses([uploadClasses.uploadRejectionFiles, className])}
      sx={sx}
      {...other}
    >
      {files?.map(({ file, errors }) => {
        const { path, size } = fileData(file);

        return (
          <ListItem key={path}>
            <ItemTitle>
              {path} - {size ? fData(size) : ''}
            </ItemTitle>

            {errors.map((error) => (
              <ItemCaption key={error.code}>- {friendlyError(error)}</ItemCaption>
            ))}
          </ListItem>
        );
      })}
    </ListRoot>
  );
}

// ----------------------------------------------------------------------

const ListRoot = styled('ul')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  border: `dashed 1px ${theme.vars.palette.error.main}`,
  backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
}));

const ListItem = styled('li')(() => ({ display: 'flex', flexDirection: 'column' }));

const ItemTitle = styled('span')(({ theme }) => ({ ...theme.typography.subtitle2 }));

const ItemCaption = styled('span')(({ theme }) => ({ ...theme.typography.caption }));
